import { FC, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import dayjs from "dayjs";
import {
	useAppVersions,
	useCreateAppVersion,
	useDeleteAppVersion,
	useSetCurrentAppVersion,
	useUpdateAppVersion,
} from "../../hooks/useAppVersions";
import {
	APP_VERSION_PLATFORMS,
	AppVersion,
	MOBILE_PLATFORMS,
	SaveAppVersion,
	platformLabel,
} from "../../domain/appVersion.interface";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { BasicDialog } from "../../components/BasicDialog";
import ConfigListToolbar from "../../components/ConfigListShell/ConfigListToolbar";
import ConfigTable, {
	type ConfigColumn,
} from "../../components/ConfigListShell/ConfigTable";
import ConfigRowActions from "../../components/ConfigListShell/ConfigRowActions";
import ConfigDeleteDialog from "../../components/ConfigListShell/ConfigDeleteDialog";
import ConfigListStates, {
	type ListState,
} from "../../components/ConfigListShell/ConfigListStates";
import { useConfigList } from "../../components/ConfigListShell/useConfigList";
import { C_BTN } from "../../components/ConfigListShell/buttonMetrics";
import AppVersionDialog from "./AppVersionDialog";

const CURRENT = "CURRENT";
const PREVIOUS = "PREVIOUS";

const STATUS_OPTIONS = [
	{ value: "all", label: "All" },
	{ value: CURRENT, label: "Current" },
	{ value: PREVIOUS, label: "Previous" },
];

const errorMessage = (error: unknown): string =>
	(error as { message?: string })?.message || "Something went wrong";

const versionLabel = (row?: AppVersion | null): string =>
	row ? `${platformLabel(row.platform)} ${row.version}` : "";

/**
 * SIMS-9441 — maintenance of the released version of each component (iOS,
 * Android, Backoffice).
 *
 * Marking an iOS / Android version as current makes the mobile app block every
 * lower version until the user updates from the store, so it is only done once
 * the store has published that version.
 */
const AppVersions: FC = () => {
	const { showSnackBar } = useFeedBack();
	const {
		data: appVersions = [],
		isLoading,
		error,
		refetch,
	} = useAppVersions();
	const createMutation = useCreateAppVersion();
	const updateMutation = useUpdateAppVersion();
	const deleteMutation = useDeleteAppVersion();
	const setCurrentMutation = useSetCurrentAppVersion();

	const [editing, setEditing] = useState<AppVersion | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [toDelete, setToDelete] = useState<AppVersion | null>(null);
	const [toMakeCurrent, setToMakeCurrent] = useState<AppVersion | null>(null);

	const list = useConfigList<AppVersion>({
		rows: appVersions,
		searchableFields: (row) => [
			row.version,
			row.apiVersion,
			row.functionalVersion,
			platformLabel(row.platform),
		],
		statusOf: (row) => (row.isCurrent ? CURRENT : PREVIOUS),
		categoriesOf: (row) => [row.platform],
		sortValue: (row, key) =>
			key === "platform"
				? platformLabel(row.platform)
				: key === "createdAt"
					? (row.createdAt ?? "")
					: row[key as keyof AppVersion],
		defaultSortKey: "createdAt",
		defaultSortDir: "desc",
		defaultPageSize: "all",
	});

	const listState: ListState = isLoading
		? "loading"
		: error
			? "load-failed"
			: appVersions.length === 0
				? "empty"
				: list.total === 0
					? "no-results"
					: "ready";

	const openDialog = (row?: AppVersion): void => {
		setEditing(row ?? null);
		setDialogOpen(true);
	};

	const closeDialog = (): void => {
		setDialogOpen(false);
		setEditing(null);
	};

	const handleSave = async (data: SaveAppVersion): Promise<void> => {
		try {
			if (editing) {
				await updateMutation.mutateAsync({ id: editing._id, data });
				showSnackBar("Version updated", "success");
			} else {
				await createMutation.mutateAsync(data);
				showSnackBar("Version added", "success");
			}
			closeDialog();
		} catch (err) {
			showSnackBar(errorMessage(err));
		}
	};

	const handleDelete = async (): Promise<void> => {
		if (!toDelete) return;
		try {
			await deleteMutation.mutateAsync(toDelete._id);
			showSnackBar("Version deleted", "success");
			setToDelete(null);
		} catch (err) {
			showSnackBar(errorMessage(err));
		}
	};

	const handleSetCurrent = async (): Promise<void> => {
		if (!toMakeCurrent) return;
		try {
			await setCurrentMutation.mutateAsync(toMakeCurrent._id);
			showSnackBar(`${versionLabel(toMakeCurrent)} is now current`, "success");
			setToMakeCurrent(null);
		} catch (err) {
			showSnackBar(errorMessage(err));
		}
	};

	const columns: ConfigColumn<AppVersion>[] = [
		{
			key: "platform",
			header: "Component",
			width: "18%",
			sortable: true,
			render: (row) => platformLabel(row.platform),
		},
		{
			key: "version",
			header: "Version",
			width: "13%",
			sortable: true,
			render: (row) => (
				<Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
					{row.version}
				</Typography>
			),
		},
		{
			key: "apiVersion",
			header: "API version",
			width: "14%",
			render: (row) => row.apiVersion || "—",
		},
		{
			key: "functionalVersion",
			header: "Functional version",
			width: "17%",
			render: (row) => row.functionalVersion || "—",
		},
		{
			key: "isCurrent",
			header: "Status",
			width: "13%",
			render: (row) =>
				row.isCurrent ? (
					<Chip label="Current" color="success" size="small" />
				) : (
					<Chip label="Previous" size="small" variant="outlined" />
				),
		},
		{
			key: "createdAt",
			header: "Created",
			width: "15%",
			sortable: true,
			render: (row) =>
				row.createdAt ? dayjs(row.createdAt).format("MMM D, YYYY") : "—",
		},
		{
			key: "actions",
			header: "",
			width: "10%",
			align: "right",
			render: (row) => (
				<ConfigRowActions
					recordName={versionLabel(row)}
					actions={[
						{
							label: "Edit",
							icon: <EditIcon sx={{ fontSize: "1.125rem" }} />,
							onClick: () => openDialog(row),
						},
						...(row.isCurrent
							? []
							: [
									{
										label: "Set as current",
										icon: (
											<CheckCircleOutlineIcon sx={{ fontSize: "1.125rem" }} />
										),
										onClick: () => setToMakeCurrent(row),
									},
								]),
						{
							label: "Delete",
							icon: <DeleteIcon sx={{ fontSize: "1.125rem" }} />,
							onClick: () => setToDelete(row),
							destructive: true,
						},
					]}
				/>
			),
		},
	];

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
			<Box display="flex" alignItems="flex-end" gap={2}>
				<Box>
					<Typography component="h1" sx={{ fontSize: "1.5rem", m: 0 }}>
						App versions
					</Typography>
					<Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
						Register a version only after the store has published it.
					</Typography>
				</Box>
				<Box sx={{ flex: 1 }} />
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={() => openDialog()}
					sx={C_BTN}
				>
					Add version
				</Button>
			</Box>

			<ConfigListToolbar
				query={list.query}
				onQueryChange={list.setQuery}
				searchPlaceholder="Search by version or component"
				status={list.status}
				onStatusChange={list.setStatus}
				statusOptions={STATUS_OPTIONS}
				categoryLabel="Component"
				category={list.category}
				onCategoryChange={list.setCategory}
				categoryOptions={APP_VERSION_PLATFORMS}
				categoryVariant="chips"
				allCategoriesLabel="All components"
			/>

			{listState !== "ready" ? (
				<ConfigListStates
					state={listState}
					view="table"
					copy={{
						loadFailedTitle: "Versions couldn't be loaded",
						loadFailedBody: "The request failed. Nothing has been changed.",
						emptyTitle: "No versions yet",
						emptyBody:
							"Register the version of each component once it is published.",
						loadingLabel: "Loading versions…",
						noResultsBody: "No version matches the current search and filters.",
						noResultsTitle: (term: string) => `No versions match “${term}”`,
						addLabel: "Add version",
					}}
					query={list.query}
					errorMessage={error ? errorMessage(error) : undefined}
					onRetry={() => refetch()}
					onClearSearch={() => list.setQuery("")}
					onClearAllFilters={() => {
						list.setQuery("");
						list.setStatus("all");
						list.setCategory("all");
					}}
					onAdd={() => openDialog()}
				/>
			) : (
				<ConfigTable<AppVersion>
					ariaLabel="App versions"
					rows={list.visible}
					columns={columns}
					rowKey={(row) => row._id}
					sortKey={list.sortKey}
					sortDir={list.sortDir}
					onSort={list.toggleSort}
				/>
			)}

			{dialogOpen ? (
				<AppVersionDialog
					appVersion={editing ?? undefined}
					isSaving={createMutation.isPending || updateMutation.isPending}
					onCancel={closeDialog}
					onSave={handleSave}
				/>
			) : null}

			{toMakeCurrent ? (
				<BasicDialog
					title="Set as current version"
					submitText="SET AS CURRENT"
					onCancel={() => setToMakeCurrent(null)}
					onSubmit={handleSetCurrent}
					isLoading={setCurrentMutation.isPending}
					sx={{ "& .MuiDialog-paper": { maxWidth: 440 } }}
				>
					<Typography data-testid="set-current-message">
						{`${versionLabel(toMakeCurrent)} will become the current ${platformLabel(toMakeCurrent.platform)} version.`}
					</Typography>
					{MOBILE_PLATFORMS.includes(toMakeCurrent.platform) ? (
						<Typography sx={{ mt: 1.5, color: "error.main", fontWeight: 600 }}>
							{`Every ${platformLabel(toMakeCurrent.platform)} device below ${toMakeCurrent.version} will be blocked until the user updates from the store. Confirm the store has already published it.`}
						</Typography>
					) : null}
				</BasicDialog>
			) : null}

			<ConfigDeleteDialog
				open={!!toDelete}
				recordName={versionLabel(toDelete)}
				recordNoun="version"
				dependencies={[]}
				onConfirm={handleDelete}
				onClose={() => setToDelete(null)}
				isDeleting={deleteMutation.isPending}
			/>
		</Box>
	);
};

export default AppVersions;
